import { ChatCreatedResponseInterface } from '../buddy-data-structures/chat-created-response.interface';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { BUDDY_AUTH_SERVICE_TOKEN } from './buddy-auth-service-token';
import { AuthService } from 'src/app/auth/auth.service';
import { BuddyConfigService } from './buddy-config.service';

@Injectable()
export class BuddyApiService {
  constructor(
    private readonly http: HttpClient,
    @Inject(BUDDY_AUTH_SERVICE_TOKEN) private readonly authService: AuthService,
    private readonly buddyConfigService: BuddyConfigService,
  ) {}

  private async getAuthToken(): Promise<string> {
    if (this.authService) {
      return (await firstValueFrom(this.authService.session$)).tokenSet
        .access_token;
    }
  }

  /**
   * Creates a new chat.
   * @param accessToken an access token to use for the request. The request will be authenticated and the chat will be assigned to the specified user.
   * @returns a promise that resolves to the chat id of the newly created chat.
   */
  async createChat(): Promise<ChatCreatedResponseInterface> {
    const useAuth = await this.buddyConfigService.buddyConfig.useAuth;
    const endpoint = await this.buddyConfigService.buddyConfig.endpoint;

    if (useAuth) {
      const accessToken = (await firstValueFrom(this.authService.session$))
        .tokenSet.access_token;
      return firstValueFrom(
        this.http.post<ChatCreatedResponseInterface>(
          new URL(`connected/chats`, endpoint).toString(),
          {},
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            withCredentials: true,
          },
        ),
      );
    } else {
      return firstValueFrom(
        this.http.post<ChatCreatedResponseInterface>(`${endpoint}/chats`, {}),
      );
    }
  }

  /**
   * Sends a message payload to the AI service for a given chat ID.
   * Utilizes authentication if configured.
   *
   * @param {string} chatId - The unique identifier for the chat session.
   * @param {string} messagePayload - The message content to be sent.
   * @returns {Promise<Response>} - A promise resolving to the AI service response.
   * @throws Will throw an error if the request fails.
   */
  async sendToAI(chatId: string, messagePayload: string) {
    console.group(`sendToAI() started for chatId: ${chatId}`);

    try {
      // Retrieve configuration values
      const { useAuth, endpoint, iconUrl, name } =
        this.buddyConfigService.buddyConfig;
      console.log(`Chat API Endpoint: ${endpoint}`);
      console.log(`Buddy Icon URL: ${iconUrl}`);
      console.log(`Buddy Name: ${name}`);
      var url = `${endpoint}/chats/${chatId}/messages`;
      const activeKey = this.buddyConfigService.getActiveConfigKey();
      if (activeKey == 'none') {
        url = `${endpoint}/chats/46eceb8c-0281-4031-bfc8-27aafbfe5bc5/messages`;
      }
      console.log(`Final Constructed URL: ${url}`);

      // Set up request headers
      let headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (useAuth) {
        console.log('Authentication is enabled. Fetching access token.');
        const accessToken = (await firstValueFrom(this.authService.session$))
          .tokenSet.access_token;
        headers.Authorization = accessToken;
        console.log(`Access token obtained: ${accessToken}`);
      } else {
        console.log('Authentication is not required.');
      }

      console.log('Preparing to send POST request to AI endpoint.');
      console.log('Request URL:', url);
      console.log('Request Headers:', headers);
      console.log('Message Payload:', messagePayload);

      // Send POST request
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: messagePayload,
      });

      // Process response
      console.log('Response received. Checking status.');
      if (!response.ok) {
        console.error(
          `Response Error [${response.status}]: ${response.statusText}`,
        );
      } else {
        console.log('Response status is OK.');
      }

      return response;
    } catch (error) {
      console.error('Error occurred during sendToAI execution:', error);
      throw error;
    } finally {
      console.groupEnd();
    }
  }
}
